import { searchApi } from 'entities/searchResult/api/searchApi';
import { SortingValue } from 'shared/lib/components/controls/sorting/sortingControl';
import { sumBy } from 'lodash-es';
import map from 'lodash-es/map';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'app/store/rootStore';
import {
  SearchResultData,
  SearchResultModel,
} from 'entities/searchResult/model/searchResultModel';

export type SearchResults = {
  other_results: SearchResultModel[];
  results_from_entity?: SearchResultModel[];
};

export type SearchResultsResponse = {
  other_results: SearchResultData[];
  results_from_entity?: SearchResultData[];
};

const makeModels = ({
  other_results,
  results_from_entity,
}: SearchResultsResponse) => {
  return {
    other_results: map(other_results, (data) => new SearchResultModel(data)),
    results_from_entity: results_from_entity
      ? map(results_from_entity, (data) => new SearchResultModel(data))
      : undefined,
  };
};

export class SearchStore {
  private _rootStore: RootStore;

  public isPending = false;
  public isError = false;

  public results: SearchResults | null = null;

  constructor({ rootStore }: { rootStore: RootStore }) {
    this._rootStore = rootStore;
    makeAutoObservable(this);
  }

  public get commonResultsCount() {
    if (this.results) {
      return sumBy(this.results.other_results, (result) => {
        return result.documentsCount + result.matchedFolders.length;
      });
    }

    return null;
  }

  public dropResults() {
    this.results = null;
  }

  public get fromEntityResultsCount() {
    if (this.results) {
      return sumBy(this.results?.results_from_entity, (result) => {
        return result.documentsCount + result.matchedFolders.length;
      });
    }

    return null;
  }

  public async getSearch(params: {
    search: string;
    folderId?: string | null;
    sectionId?: string | null;
    sort: SortingValue;
    search_exact: boolean;
  }) {
    this._rootStore.stateStore.isSearchLoading = true;

    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      if (params.folderId || params.sectionId) {
        const resultsData = await searchApi.getByEntityResult({
          search: params.search,
          sort: params.sort,
          folderId: params.folderId,
          sectionId: params.sectionId,
          search_exact: params.search_exact,
        });

        runInAction(() => {
          this.results = makeModels(resultsData);
          this.isPending = false;
          this._rootStore.stateStore.isSearchLoading = false;
        });
      } else {
        const resultsData = await searchApi.getCommonResults(
          params.search,
          params.sort,
          params.search_exact
        );

        runInAction(() => {
          this.results = makeModels(resultsData);
          this.isPending = false;
          this._rootStore.stateStore.isSearchLoading = false;
        });
      }
    } catch (e) {
      runInAction(() => {
        this._rootStore.stateStore.isSearchLoading = false;
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }
}
